import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Upload, Download, FileText } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

interface BulkImportProps {
  type: 'tournaments' | 'teams' | 'players' | 'matches';
  onImport: (data: any[]) => void;
  sampleData: any[];
  headers: string[];
  requiredColumns?: string[];
}

const BulkImport: React.FC<BulkImportProps> = ({ type, onImport, sampleData, headers, requiredColumns = [] }) => {
  const [file, setFile] = useState<File | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const downloadSample = () => {
    // Update sample data based on type
    let updatedSampleData = [...sampleData];
    
    if (type === 'teams') {
      updatedSampleData = [
        { name: 'Mumbai Indians', shortCode: 'MI', color: '#004BA0', tournamentId: 'T-0XX001' }
      ];
    }

    // Add red star (*) to required column headers and make them bold
    const formattedHeaders = headers.map(header => {
      const isRequired = requiredColumns.includes(header);
      return isRequired ? `"${header}*"` : `"${header}"`;
    });

    const csvContent = [
      formattedHeaders.join(','),
      ...updatedSampleData.map(row => headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}_sample.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
    } else {
      toast({
        title: "Invalid file type",
        description: "Please select a CSV file.",
        variant: "destructive",
      });
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast({
        title: "No file selected",
        description: "Please select a CSV file to import.",
        variant: "destructive",
      });
      return;
    }

    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      // Remove asterisk and quotes from headers when processing
      const csvHeaders = lines[0].split(',').map(h => h.trim().replace(/"/g, '').replace(/\*$/, ''));
      
      const data = lines.slice(1).map(line => {
        // Handle quoted values that might contain commas
        const values = [];
        let current = '';
        let inQuotes = false;
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim());
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim());

        const row: any = {};
        csvHeaders.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      // Validate required fields
      const missingRequiredFields = data.some(row => 
        requiredColumns.some(required => !row[required])
      );

      if (missingRequiredFields) {
        toast({
          title: "Import failed",
          description: "Please fill in all required fields marked with *",
          variant: "destructive",
        });
        return;
      }

      onImport(data);
      setIsOpen(false);
      setFile(null);
      toast({
        title: "Import successful",
        description: `${data.length} ${type} imported successfully.`,
      });
    } catch (error) {
      toast({
        title: "Import failed",
        description: "There was an error processing the CSV file.",
        variant: "destructive",
      });
    }
  };

  const getInstructions = () => {
    switch (type) {
      case 'teams':
        return 'For teams, use the Tournament ID (e.g., T-0XX001) in the tournamentId column.';
      case 'players':
        return 'For players, use the Team ID (e.g., TM-0XX001) in the teamId column.';
      default:
        return '';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Upload className="mr-2 h-4 w-4" />
          Bulk Import
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bulk Import {type.charAt(0).toUpperCase() + type.slice(1)}</DialogTitle>
          <DialogDescription>
            Upload a CSV file to import multiple {type} at once.
            {getInstructions() && (
              <>
                <br />
                <strong>Note:</strong> {getInstructions()}
              </>
            )}
            {requiredColumns.length > 0 && (
              <>
                <br />
                <strong>Required fields are marked with <span style={{ color: 'red' }}>*</span></strong>
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Button variant="outline" onClick={downloadSample} className="w-full">
              <Download className="mr-2 h-4 w-4" />
              Download Sample CSV
            </Button>
          </div>
          <div>
            <Label htmlFor="csv-file">Select CSV File</Label>
            <Input
              id="csv-file"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="mt-1"
            />
          </div>
          {file && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <FileText className="h-4 w-4" />
              {file.name}
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={handleImport} disabled={!file} className="flex-1">
              Import CSV
            </Button>
            <Button variant="outline" onClick={() => setIsOpen(false)} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BulkImport;
